import React, { useState, Suspense } from 'react';
import {
  func, string, oneOf, shape, number
} from 'prop-types';
import { Col } from '@thd-olt-component-react/core-ui';
import { useDataModel } from '@thd-nucleus/data-sources';
import { Carousel } from '@thd-olt-component-react/carousel';
import { dataModel } from './ratings-and-reviews-data-model';
import * as analytics from '../../analytics';
import './customer-image-carousel.scss';

const analyticsData = () => {
  analytics.track('click', {
    element: 'media'
  });
};

const Overlay = React.lazy(() => import(/* webpackChunkName: "overlay" */ '@thd-olt-component-react/core-ui')
  .then((module) => ({ default: module.Overlay })));
// eslint-disable-next-line max-len
const CustomerImageOverlay = React.lazy(() => import(/* webpackChunkName: "customer-image-overlay" */ '@thd-olt-component-react/customer-image-overlay')
  .then((module) => ({ default: module.CustomerImageOverlay })));

export const CustomerImageCarousel = ({
  minimumNumberOfImages, itemId, channel, tooltip, hasPhotos
}) => {
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [imageId, setImageId] = useState(null);
  const opts = {
    variables: {
      itemId
    },
    ssr: false
  };

  const { data, error, loading } = useDataModel('reviewPhotos', opts);
  if ((error && !data) || !data || loading) {
    return null;
  }
  const { reviewPhotos } = data || {};
  const { Results } = reviewPhotos || {};
  if (!(Results || []).length) {
    hasPhotos();
    return null;
  }

  const carouselPhotos = Results.reduce((images, review) => {
    return images.concat(review.Photos);
  }, []);

  const imgOverlayPhotoId = carouselPhotos[imageId]?.Id;

  const onCarouselClick = (id) => {
    setImageId(id.toString());
    if (tooltip) {
      tooltip.hide();
    }
    analyticsData();
    setOverlayOpen(true);
  };
  if (carouselPhotos.length <= minimumNumberOfImages) {
    hasPhotos();
    return null;
  }
  return (
    <>
      <div className="product-details__secondary">
        <h3 className="product-details__secondary__heading">Customer Images</h3>
      </div>
      <div className="zone-customer-images">
        <Col xs={12}>
          <Carousel
            multiItem
            className="zone-customer-images__tooltip-carousel"
            showDots={false}
            onClickItem={(itemIndex) => setImageId(itemIndex)}
            selectedItem={imageId}
            itemWidthDesktop="40px"
          >
            {
              carouselPhotos.map((photo, photoIndex) => {
                const thumbNailImg = photo.Sizes ? `url(${photo.Sizes.normal.Url})` : '';
                return (
                  <div
                    key={`${photoIndex}`}
                    className="zone-customer-images__tooltip-carousel__carousel-normal"
                  >
                    <button
                      aria-label="img"
                      type="button"
                      style={{
                        backgroundImage: (thumbNailImg)
                      }}
                      onClick={() => onCarouselClick(photo.Id)}
                    />
                  </div>
                );
              })
            }
          </Carousel>
        </Col>
        {overlayOpen && (
          <Suspense fallback={(<div />)}>
            <Overlay
              modalClass="customer-image-carousel__overlay"
              scrollable={false}
              medium
              open={overlayOpen}
              onClose={() => setOverlayOpen(false)}
            >
              {imgOverlayPhotoId ? (
                <CustomerImageOverlay
                  channel={channel}
                  photos={reviewPhotos}
                  selectedPhotoID={imgOverlayPhotoId}
                  onClose={() => {
                    setOverlayOpen(false);
                    setImageId(null);
                  }}
                  loopPhotos={false}
                  onInteraction={analyticsData}
                />
              ) : null}
            </Overlay>
          </Suspense>
        )}

      </div>
    </>
  );
};

CustomerImageCarousel.displayName = 'CustomerImageCarousel';

CustomerImageCarousel.propTypes = {
  channel: oneOf(['desktop', 'mobile']),
  itemId: string.isRequired,
  minimumNumberOfImages: number,
  tooltip: shape({}),
  hasPhotos: func.isRequired,
};

CustomerImageCarousel.defaultProps = {
  channel: 'desktop',
  minimumNumberOfImages: 3,
  tooltip: {},
};

CustomerImageCarousel.dataModel = {
  reviewPhotos: dataModel?.reviewPhotos
};
